/**
 * Base Field Renderer
 * Common functionality for all field types
 */

import { FormatUtils } from '../utils/format_utils.js';
import { Security } from '../core/security.js';

export class FieldRenderer {
  static translator = null;

  /**
   * Set translator instance
   * @param {Translator} translator - Translator instance
   */
  static setTranslator(translator) {
    this.translator = translator;
  }

  /**
   * Replace placeholders in a string: {{key}}, {{behalf.key}}, {{submitter.key}}, {{last_updated.key}}.
   * Use for area toc/description or any label that may contain these placeholders.
   * @param {string} content - Raw string (e.g. area.toc or area.description)
   * @param {Object} activeCustomer - Active (form) customer data
   * @param {Object} behalfContext - Behalf context (optional) with behalf_customer, submitter_customer, last_updated_customer
   * @returns {string} String with placeholders replaced (HTML-escaped)
   */
  static replacePlaceholdersInString(content, activeCustomer = {}, behalfContext = null) {
    if (typeof content !== 'string') return '';
    const getEscaped = (obj, key) => {
      if (!obj || typeof obj !== 'object') return '';
      if (!/^[a-zA-Z0-9_]+$/.test(key)) return '';
      const val = obj[key];
      if (val === undefined || val === null) return '';
      return FormatUtils.escapeHtml(String(val));
    };
    let out = content;
    out = out.replace(/\{\{behalf\.([a-zA-Z0-9_]+)\}\}/g, (_, key) =>
      getEscaped(behalfContext?.behalf_customer, key)
    );
    out = out.replace(/\{\{submitter\.([a-zA-Z0-9_]+)\}\}/g, (_, key) =>
      getEscaped(behalfContext?.submitter_customer, key)
    );
    out = out.replace(/\{\{last_updated\.([a-zA-Z0-9_]+)\}\}/g, (_, key) =>
      getEscaped(behalfContext?.last_updated_customer, key)
    );
    out = out.replace(/\{\{([a-zA-Z0-9_]+)\}\}/g, (_, key) =>
      getEscaped(activeCustomer, key)
    );
    return out;
  }

  /**
   * Render field based on type
   * @param {Object} fieldConfig - Field configuration
   * @param {Object} activeCustomer - Active customer data
   * @param {Object} behalfContext - Behalf context (optional)
   * @param {Object} formSourceContext - Form entries by form_id for "another form" source (optional)
   * @param {Object} formOptions - Form-level options (e.g. date_input_format for Flatpickr)
   * @returns {string} HTML string
   */
  static render(fieldConfig, activeCustomer = {}, behalfContext = null, formSourceContext = null, formOptions = {}) {
    if (!fieldConfig || !fieldConfig.id) {
      return '';
    }

    const type = fieldConfig.type || 'input';
    
    let html = '';
    try {
      switch (type) {
        case 'h':
          html = this.renderHeading(fieldConfig);
          break;
        case 't':
          html = this.renderText(fieldConfig, activeCustomer, behalfContext, formSourceContext);
          break;
        case 'input':
        case 'email':
        case 'date':
          html = this.renderInput(fieldConfig, activeCustomer, behalfContext, formSourceContext, formOptions);
          break;
        case 'phone':
          html = this.renderPhone(fieldConfig, activeCustomer, behalfContext, formSourceContext);
          break;
        case 'number':
          html = this.renderNumber(fieldConfig, activeCustomer, behalfContext, formSourceContext);
          break;
        case 'textarea':
          html = this.renderTextarea(fieldConfig, activeCustomer, behalfContext, formSourceContext);
          break;
        case 'select':
          html = this.renderSelect(fieldConfig, activeCustomer, behalfContext, formSourceContext);
          break;
        case 'checkbox':
        case 'radio':
          html = this.renderCheckboxRadio(fieldConfig, activeCustomer, behalfContext, formSourceContext);
          break;
        case 'file':
          html = this.renderFile(fieldConfig, activeCustomer, behalfContext, formSourceContext);
          break;
        case 'signature':
          html = this.renderSignature(fieldConfig, activeCustomer, formSourceContext);
          break;
        case 'calculated':
          html = this.renderCalculated(fieldConfig, activeCustomer);
          break;
        case 'shadow':
          html = this.renderShadow(fieldConfig, activeCustomer);
          break;
        case 'button':
          html = this.renderButton(fieldConfig, activeCustomer);
          break;
        case 'customers_based':
          html = this.renderCustomersBased(fieldConfig, activeCustomer, behalfContext, formSourceContext);
          break;
        default:
          return '';
      }
    } catch (error) {
      return '';
    }

    return html || '';
  }

  /**
   * Get field value from customer data or from another form's entry
   * @param {Object} fieldConfig - Field configuration
   * @param {Object} activeCustomer - Active customer data
   * @param {Object} behalfContext - Behalf context (optional)
   * @param {Object} formSourceContext - Form entries by form_id (optional)
   * @returns {string|Array}
   */
  static getDefaultValue(fieldConfig, activeCustomer, behalfContext = null, formSourceContext = null) {
    // Source from another form (user filled)
    if (fieldConfig.field_sync_source === 'form') {
      const formId = fieldConfig.field_sync_form_id;
      const fieldId = fieldConfig.field_sync_form_field_id;
      if (!formId || !fieldId || !formSourceContext || !formSourceContext[formId]) {
        return '';
      }
      const entry = formSourceContext[formId];
      const formData = entry.form_data || entry.formData || {};
      let value = formData[fieldId];
      if (value === undefined && Array.isArray(formData[fieldId + '[]'])) {
        value = formData[fieldId + '[]'];
      }
      if (value === undefined) {
        return '';
      }
      return typeof value === 'string' ? value : (Array.isArray(value) ? value : String(value));
    }

    // Determine which customer data to use based on field_sync_source
    let customerData = activeCustomer;
    
    if (behalfContext && fieldConfig.field_sync_source) {
      if (fieldConfig.field_sync_source === 'behalf') {
        customerData = behalfContext.behalf_customer || activeCustomer;
      } else if (fieldConfig.field_sync_source === 'submitter') {
        customerData = behalfContext.submitter_customer || activeCustomer;
      } else if (fieldConfig.field_sync_source === 'last_updated') {
        customerData = behalfContext.last_updated_customer || activeCustomer;
      } else if (fieldConfig.field_sync_source === 'none') {
        return ''; // No auto-fill
      }
    }
    
    // Backward compatibility: if field_sync_source is not set but field_users_terms is true, default to behalf
    if (behalfContext && !fieldConfig.field_sync_source && fieldConfig.field_users_terms) {
      customerData = behalfContext.behalf_customer || activeCustomer;
    }
    
    if (!fieldConfig.defaultValue || !customerData) {
      return '';
    }

    if (fieldConfig.usersLimit && fieldConfig.usersLimit.length > 0) {
      for (const limit of fieldConfig.usersLimit) {
        if (String(customerData[limit.value] || '').toLowerCase() === String(limit.input || '').toLowerCase()) {
          const raw = customerData[fieldConfig.defaultValue];
          return (raw === undefined || raw === null) ? '' : String(raw);
        }
      }
      return '';
    }

    const raw = customerData[fieldConfig.defaultValue];
    return (raw === undefined || raw === null) ? '' : String(raw);
  }

  /**
   * Get value for rendering: getDefaultValue with defaultValueIfEmpty fallback when empty
   * @param {Object} fieldConfig - Field configuration
   * @param {Object} activeCustomer - Active customer data
   * @param {Object} behalfContext - Behalf context (optional)
   * @param {Object} formSourceContext - Form entries by form_id (optional)
   * @returns {string|Array}
   */
  static getValueForRender(fieldConfig, activeCustomer, behalfContext = null, formSourceContext = null) {
    const raw = this.getDefaultValue(fieldConfig, activeCustomer, behalfContext, formSourceContext);
    const isEmpty = raw === '' || raw == null || (Array.isArray(raw) && raw.length === 0);
    if (isEmpty && fieldConfig.defaultValueIfEmpty != null && String(fieldConfig.defaultValueIfEmpty).trim() !== '') {
      return fieldConfig.defaultValueIfEmpty;
    }
    // Radio/select/checkbox: treat defaultValue as literal when it matches an option (e.g. API or "default value if empty" stored in defaultValue)
    if (isEmpty && fieldConfig.defaultValue != null && String(fieldConfig.defaultValue).trim() !== '' && (fieldConfig.type === 'radio' || fieldConfig.type === 'checkbox' || fieldConfig.type === 'select')) {
      const values = fieldConfig.values || [];
      const literal = String(fieldConfig.defaultValue).trim();
      const matchesOption = values.some(v => String(v).toLowerCase() === literal.toLowerCase());
      if (matchesOption) {
        return literal;
      }
    }
    return raw;
  }

  /**
   * Compute age in full years from a date string (e.g. birth date) to today.
   * Uses entire date: if birthday has not occurred this year, age is one less.
   * @param {string} dateString - ISO date string or parseable date
   * @returns {string} Age as string, or '' if invalid/empty
   */
  static computeAgeFromDateString(dateString) {
    if (dateString == null || String(dateString).trim() === '') return '';
    const birth = new Date(dateString);
    if (Number.isNaN(birth.getTime())) return '';
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    if (age < 0) return '';
    return String(age);
  }

  /**
   * Get visibility class
   * @param {Object} fieldConfig - Field configuration
   * @returns {string}
   */
  static getVisibilityClass(fieldConfig) {
    const visibleSettings = fieldConfig.visibleSettings || 'y';
    
    if (visibleSettings === 'c' || visibleSettings === 'n') {
      return 'd-none';
    }
    
    return 'd-block';
  }

  /**
   * Get required attribute
   * @param {Object} fieldConfig - Field configuration
   * @param {string} visibilityClass - Visibility class
   * @returns {string}
   */
  static getRequiredAttr(fieldConfig, visibilityClass) {
    if (fieldConfig.required !== 'y') {
      return '';
    }
    if (visibilityClass === 'd-block') {
      return 'required data-required="required"';
    }
    return 'data-required="required"';
  }

  /**
   * Render heading field
   * @param {Object} fieldConfig - Field configuration
   * @returns {string}
   */
  static renderHeading(fieldConfig) {
    const heading = fieldConfig.heading || 'h3';
    const dbl = this.getVisibilityClass(fieldConfig);
    const alignment = fieldConfig.alignment || '';
    
    return `<${heading} class="${dbl} ${alignment}">${FormatUtils.escapeHtml(fieldConfig.title || '')}</${heading}>`;
  }

  /**
   * Render text field with optional placeholders: {{key}} from form customer,
   * {{behalf.key}}, {{submitter.key}}, {{last_updated.key}} from behalf context.
   * All replacement values are HTML-escaped.
   * @param {Object} fieldConfig - Field configuration
   * @param {Object} activeCustomer - Active (form) customer data
   * @param {Object} behalfContext - Behalf context (optional)
   * @param {Object} formSourceContext - Form entries by form_id (optional)
   * @returns {string}
   */
  static renderText(fieldConfig, activeCustomer = {}, behalfContext = null, formSourceContext = null) {
    let content = fieldConfig.content || '';
    if (typeof content !== 'string') return '';

    const getEscaped = (obj, key) => {
      if (!obj || typeof obj !== 'object') return '';
      if (!/^[a-zA-Z0-9_]+$/.test(key)) return '';
      const val = obj[key];
      if (val === undefined || val === null) return '';
      return FormatUtils.escapeHtml(String(val));
    };

    // Replace prefixed placeholders first (longer patterns)
    content = content.replace(/\{\{behalf\.([a-zA-Z0-9_]+)\}\}/g, (_, key) =>
      getEscaped(behalfContext?.behalf_customer, key)
    );
    content = content.replace(/\{\{submitter\.([a-zA-Z0-9_]+)\}\}/g, (_, key) =>
      getEscaped(behalfContext?.submitter_customer, key)
    );
    content = content.replace(/\{\{last_updated\.([a-zA-Z0-9_]+)\}\}/g, (_, key) =>
      getEscaped(behalfContext?.last_updated_customer, key)
    );
    // Replace {{key}} with form customer (activeCustomer)
    content = content.replace(/\{\{([a-zA-Z0-9_]+)\}\}/g, (_, key) =>
      getEscaped(activeCustomer, key)
    );

    return content;
  }

  /**
   * Render input field
   * @param {Object} fieldConfig - Field configuration
   * @param {Object} activeCustomer - Active customer data
   * @param {Object} behalfContext - Behalf context (optional)
   * @param {Object} formSourceContext - Form entries by form_id (optional)
   * @param {Object} formOptions - Form-level options (e.g. date_input_format for Flatpickr)
   * @returns {string}
   */
  static renderInput(fieldConfig, activeCustomer, behalfContext = null, formSourceContext = null, formOptions = {}) {
    const isDateField = fieldConfig.type === 'date';
    const inputType = fieldConfig.type === 'input' ? 'text' : (isDateField ? 'text' : fieldConfig.type);
    const dbl = this.getVisibilityClass(fieldConfig);
    const required = this.getRequiredAttr(fieldConfig, dbl);
    const readonly = fieldConfig.readonly === 'y' ? 'readonly' : '';
    const placeholder = FormatUtils.escapeHtml(fieldConfig.placeholder || fieldConfig.placehoder || '');
    const inputFunctionValue = fieldConfig.inputFunction || '';
    const dateOffsets = {
      forceTodayDate: 0,
      forceTodayDatePlus1Month: 1,
      forceTodayDatePlus2Months: 2,
      forceTodayDatePlus3Months: 3,
      forceTodayDatePlus4Months: 4,
      forceTodayDatePlus6Months: 6
    };

    const addMonthsToDate = (dateString, months) => {
      const baseDate = dateString ? new Date(dateString) : new Date();
      if (Number.isNaN(baseDate.getTime())) return '';
      baseDate.setMonth(baseDate.getMonth() + months);
      return FormatUtils.formatDate(baseDate, 'YYYY-MM-DD');
    };

    const shouldApplyOffset =
      isDateField && Object.prototype.hasOwnProperty.call(dateOffsets, inputFunctionValue);

    const computedDateValue = shouldApplyOffset
      ? addMonthsToDate(FormatUtils.getTodayDate(), dateOffsets[inputFunctionValue])
      : '';

    const rawValue = shouldApplyOffset ? computedDateValue : this.getValueForRender(fieldConfig, activeCustomer, behalfContext, formSourceContext);
    const value = FormatUtils.escapeHtml(Array.isArray(rawValue) ? rawValue.join(', ') : String(rawValue ?? ''));
    const alignment = fieldConfig.alignment || '';
    const fnVal = fieldConfig.inputFunction || fieldConfig.input_function;
    const inputFunction = fnVal ? `data-input-function="${fnVal}"` : '';
    const inputToday = Object.prototype.hasOwnProperty.call(dateOffsets, inputFunctionValue)
      ? `data-today-date="${dateOffsets[inputFunctionValue]}"`
      : '';

    let dateLimitations = '';
    let dataMinDate = '';
    let dataMaxDate = '';
    if (isDateField && fieldConfig.dateLimitation) {
      const today = FormatUtils.getTodayDate();
      switch (fieldConfig.dateLimitation) {
        case 'past':
          dateLimitations = `max="${today}"`;
          dataMaxDate = `data-max-date="${today}"`;
          break;
        case 'future':
          dateLimitations = `min="${today}"`;
          dataMinDate = `data-min-date="${today}"`;
          break;
        case 'between':
          if (fieldConfig.dateFromLimit && fieldConfig.dateToLimit) {
            dateLimitations = `min="${fieldConfig.dateFromLimit}" max="${fieldConfig.dateToLimit}"`;
            dataMinDate = `data-min-date="${fieldConfig.dateFromLimit}"`;
            dataMaxDate = `data-max-date="${fieldConfig.dateToLimit}"`;
          }
          break;
      }
    }

    const dateInputFormat = formOptions.date_input_format || 'Y-m-d';
    const flatpickrAttrs = isDateField
      ? `data-flatpickr="true" data-date-format="Y-m-d" data-alt-format="${FormatUtils.escapeHtml(dateInputFormat)}" ${dataMinDate} ${dataMaxDate}${shouldApplyOffset ? ` data-default-date="${FormatUtils.escapeHtml(computedDateValue)}"` : ''}`
      : '';

    const title = FormatUtils.escapeHtml(fieldConfig.title || '');
    const titleWithRequired = fieldConfig.required === 'y' && !title.includes('Required Field')
      ? `${title} *`
      : title;

    const maxlength = (fieldConfig.maxchars && !isNaN(parseInt(fieldConfig.maxchars, 10)) && parseInt(fieldConfig.maxchars, 10) > 0)
      ? `maxlength="${parseInt(fieldConfig.maxchars, 10)}"`
      : '';

    return `
      <div class="form-group ${dbl}">
        <label for="${fieldConfig.id}">${titleWithRequired}</label>
        <input 
          id="${fieldConfig.id}" 
          name="${fieldConfig.id}" 
          class="form-control ${alignment}" 
          type="${inputType}" 
          placeholder="${placeholder}"
          ${readonly}
          ${required}
          ${inputFunction}
          ${inputToday}
          ${flatpickrAttrs}
          value="${value}"
          ${dateLimitations}
          ${maxlength}
        />
        <span class="d-none error_message" id="err_${fieldConfig.id}">Invalid value entered</span>
      </div>
    `;
  }

  /**
   * Render phone field
   * @param {Object} fieldConfig - Field configuration
   * @param {Object} activeCustomer - Active customer data
   * @param {Object} behalfContext - Behalf context (optional)
   * @returns {string}
   */
  static renderPhone(fieldConfig, activeCustomer, behalfContext = null, formSourceContext = null) {
    const dbl = this.getVisibilityClass(fieldConfig);
    const required = this.getRequiredAttr(fieldConfig, dbl);
    const readonly = fieldConfig.readonly === 'y' ? 'readonly' : '';
    const rawValue = this.getValueForRender(fieldConfig, activeCustomer, behalfContext, formSourceContext);
    const value = FormatUtils.escapeHtml(Array.isArray(rawValue) ? rawValue.join(', ') : String(rawValue ?? ''));
    const title = FormatUtils.escapeHtml(fieldConfig.title || '');
    const titleWithRequired = fieldConfig.required === 'y' && !title.includes('Required Field') 
      ? `${title} *` 
      : title;

    return `
      <div class="form-group ${dbl}">
        <label for="${fieldConfig.id}">${titleWithRequired}</label>
        <input 
          id="${fieldConfig.id}" 
          name="${fieldConfig.id}" 
          class="form-control phone-input" 
          type="tel"
          ${readonly}
          ${required}
          value="${value}"
        />
        <span class="d-none error_message" id="err_${fieldConfig.id}">Please enter a valid phone number</span>
      </div>
    `;
  }

  /**
   * Render number field
   * @param {Object} fieldConfig - Field configuration
   * @param {Object} activeCustomer - Active customer data
   * @param {Object} behalfContext - Behalf context (optional)
   * @returns {string}
   */
  static renderNumber(fieldConfig, activeCustomer, behalfContext = null, formSourceContext = null) {
    const dbl = this.getVisibilityClass(fieldConfig);
    const required = this.getRequiredAttr(fieldConfig, dbl);
    const readonly = fieldConfig.readonly === 'y' ? 'readonly' : '';
    const placeholder = FormatUtils.escapeHtml(fieldConfig.placeholder || fieldConfig.placehoder || '');
    let rawValue;
    if (fieldConfig.inputFunction === 'yearDifferenceFromField' && fieldConfig.defaultValue) {
      const dateStr = this.getDefaultValue(fieldConfig, activeCustomer, behalfContext, formSourceContext);
      rawValue = this.computeAgeFromDateString(dateStr);
    } else {
      rawValue = this.getValueForRender(fieldConfig, activeCustomer, behalfContext, formSourceContext);
    }
    const value = FormatUtils.escapeHtml(Array.isArray(rawValue) ? rawValue.join(', ') : String(rawValue ?? ''));
    const alignment = fieldConfig.alignment || '';
    const min = fieldConfig.min || -90000000000;
    const max = fieldConfig.max || 999999999999;
    const step = fieldConfig.step || 1;
    const fnVal = fieldConfig.inputFunction || fieldConfig.input_function;
    const inputFunction = fnVal ? `data-input-function="${fnVal}"` : '';
    const yearDiffAttrs = (fieldConfig.inputFunction === 'yearDifferenceFromField')
      ? `data-year-diff-source="${FormatUtils.escapeHtml(fieldConfig.defaultValue || '')}" data-field-sync-source="${FormatUtils.escapeHtml(fieldConfig.field_sync_source || 'behalf')}"`
      : '';
    const title = FormatUtils.escapeHtml(fieldConfig.title || '');
    const titleWithRequired = fieldConfig.required === 'y' && !title.includes('Required Field') 
      ? `${title} *` 
      : title;

    return `
      <div class="form-group ${dbl}">
        <label for="${fieldConfig.id}">${titleWithRequired}</label>
        <input
          id="${fieldConfig.id}"
          name="${fieldConfig.id}"
          ${inputFunction}
          ${yearDiffAttrs}
          class="form-control ${alignment}"
          type="number"
          placeholder="${placeholder}"
          ${readonly}
          ${required}
          min="${min}"
          max="${max}"
          step="${step}"
          value="${value}"
        />
        <span class="d-none error_message" id="err_${fieldConfig.id}">Please enter ${title} in range ${min}-${max} with steps of ${step}</span>
      </div>
    `;
  }

  /**
   * Render textarea field
   * @param {Object} fieldConfig - Field configuration
   * @param {Object} activeCustomer - Active customer data
   * @param {Object} behalfContext - Behalf context (optional)
   * @returns {string}
   */
  static renderTextarea(fieldConfig, activeCustomer, behalfContext = null, formSourceContext = null) {
    const dbl = this.getVisibilityClass(fieldConfig);
    const required = this.getRequiredAttr(fieldConfig, dbl);
    const readonly = fieldConfig.readonly === 'y' ? 'readonly' : '';
    const placeholder = FormatUtils.escapeHtml(fieldConfig.placeholder || fieldConfig.placehoder || '');
    const rawValue = this.getValueForRender(fieldConfig, activeCustomer, behalfContext, formSourceContext);
    const value = FormatUtils.escapeHtml(Array.isArray(rawValue) ? rawValue.join(', ') : String(rawValue ?? ''));
    const alignment = fieldConfig.alignment || '';
    const title = FormatUtils.escapeHtml(fieldConfig.title || '');
    const titleWithRequired = fieldConfig.required === 'y' && !title.includes('Required Field') 
      ? `${title} *` 
      : title;

    const maxlength = (fieldConfig.maxchars && !isNaN(parseInt(fieldConfig.maxchars, 10)) && parseInt(fieldConfig.maxchars, 10) > 0)
      ? `maxlength="${parseInt(fieldConfig.maxchars, 10)}"`
      : '';

    return `
      <div class="form-group ${dbl}">
        <label for="${fieldConfig.id}">${titleWithRequired}</label>
        <textarea 
          id="${fieldConfig.id}" 
          name="${fieldConfig.id}" 
          class="form-control ${alignment}" 
          placeholder="${placeholder}"
          ${readonly}
          ${required}
          ${maxlength}
        >${value}</textarea>
      </div>
    `;
  }

  /**
   * Render select field
   * @param {Object} fieldConfig - Field configuration
   * @param {Object} activeCustomer - Active customer data
   * @param {Object} behalfContext - Behalf context (optional)
   * @returns {string}
   */
  static renderSelect(fieldConfig, activeCustomer, behalfContext = null, formSourceContext = null) {
    const dbl = this.getVisibilityClass(fieldConfig);
    const required = this.getRequiredAttr(fieldConfig, dbl);
    const disabled = fieldConfig.readonly === 'y' ? 'disabled' : '';
    const inputFunction = fieldConfig.inputFunction === 'israeliBankSelector' ? 'data-input-function="israeliBankSelector"' : '';
    
    // Get placeholder, fallback to translated "Please select" if empty/undefined
    // Support both 'placeholder' and 'placehoder' (typo) for backward compatibility
    let placeholderText = fieldConfig.placeholder || fieldConfig.placehoder || '';
    if (!placeholderText || placeholderText.trim() === '') {
      const t = this.translator ? (key) => this.translator.t(key) : ((key) => key);
      placeholderText = t('errors.please_select_option');
      // Final fallback if translation fails or returns undefined
      if (!placeholderText || placeholderText === 'errors.please_select_option') {
        placeholderText = 'Please select an option';
      }
    }
    const placeholder = FormatUtils.escapeHtml(placeholderText);
    
    const rawValue = this.getValueForRender(fieldConfig, activeCustomer, behalfContext, formSourceContext);
    const value = Array.isArray(rawValue) ? rawValue[0] : rawValue;
    const alignment = fieldConfig.alignment || '';
    const title = FormatUtils.escapeHtml(fieldConfig.title || '');
    const titleWithRequired = fieldConfig.required === 'y' && !title.includes('Required Field') 
      ? `${title} *` 
      : title;

    const options = (fieldConfig.inputFunction === 'israeliBankSelector' ? [] : (fieldConfig.values || [])).map((dval, index) => {
      const blockerAttr = (fieldConfig.blocker_options && fieldConfig.blocker_options[index] === '1')
        ? 'data-blocker="1"'
        : '';
      const alertContent = fieldConfig.alerts_options && fieldConfig.alerts_options[index];
      const alertAttr = (alertContent != null && String(alertContent).trim() !== '')
        ? `data-alert="${FormatUtils.escapeHtml(fieldConfig.alerts_options[index])}"`
        : '';
      const lineItemId = fieldConfig.lineItemID_options ? fieldConfig.lineItemID_options[index] : undefined;
      const lineItemAttr = lineItemId !== undefined ? `data-lineitemid="${FormatUtils.escapeHtml(String(lineItemId))}"` : '';
      const valueStr = value != null ? String(value).trim() : '';
      const selected = (dval === value || (valueStr !== '' && String(dval).toLowerCase() === valueStr.toLowerCase())) ? 'selected' : '';
      const escapedValue = FormatUtils.escapeHtml(dval);
      
      return `<option ${blockerAttr} ${alertAttr} ${lineItemAttr} value="${escapedValue}" ${selected}>${escapedValue}</option>`;
    }).join('');

    return `
      <div class="form-group select-wrapper ${dbl}">
        <label for="${fieldConfig.id}">${titleWithRequired}</label>
        <select 
          id="${fieldConfig.id}" 
          name="${fieldConfig.id}" 
          ${inputFunction}
          ${disabled}
          ${required}
          placeholder="${placeholder}"
          class="wide select tqf_select form-control ${alignment}"
        >
          <option value="">${placeholder}</option>
          ${options}
        </select>
      </div>
    `;
  }

  /**
   * Render customers_based field (select populated from backend config).
   * Uses fieldConfig.customers_options (array of {value, label}) injected by form_configuration.
   * @param {Object} fieldConfig - Field configuration
   * @param {Object} activeCustomer - Active customer data
   * @param {Object} behalfContext - Behalf context (optional)
   * @param {Object} formSourceContext - Form entries by form_id (optional)
   * @returns {string}
   */
  static renderCustomersBased(fieldConfig, activeCustomer, behalfContext = null, formSourceContext = null) {
    const dbl = this.getVisibilityClass(fieldConfig);
    const required = this.getRequiredAttr(fieldConfig, dbl);
    const disabled = fieldConfig.readonly === 'y' ? 'disabled' : '';
    let placeholderText = fieldConfig.placeholder || fieldConfig.placehoder || '';
    if (!placeholderText || placeholderText.trim() === '') {
      const t = this.translator ? (key) => this.translator.t(key) : ((key) => key);
      placeholderText = t('errors.please_select_option') || 'Please select an option';
    }
    const placeholder = FormatUtils.escapeHtml(placeholderText);
    const rawValue = this.getValueForRender(fieldConfig, activeCustomer, behalfContext, formSourceContext);
    const value = Array.isArray(rawValue) ? rawValue[0] : rawValue;
    const valueStr = value != null ? String(value).trim() : '';
    const alignment = fieldConfig.alignment || '';
    const title = FormatUtils.escapeHtml(fieldConfig.title || '');
    const titleWithRequired = fieldConfig.required === 'y' && !title.includes('Required Field')
      ? `${title} *`
      : title;

    const options = (fieldConfig.customers_options || []).map((opt) => {
      const optValue = opt.value != null ? String(opt.value) : '';
      const optLabel = opt.label != null ? FormatUtils.escapeHtml(String(opt.label)) : optValue;
      const selected = (valueStr !== '' && optValue === valueStr) ? 'selected' : '';
      const customerData = opt.customer && typeof opt.customer === 'object'
        ? ` data-customer="${encodeURIComponent(JSON.stringify(opt.customer))}"`
        : '';
      return `<option value="${FormatUtils.escapeHtml(optValue)}"${customerData} ${selected}>${optLabel}</option>`;
    }).join('');

    return `
      <div class="form-group select-wrapper customers-based-wrapper ${dbl}">
        <label for="${fieldConfig.id}">${titleWithRequired}</label>
        <select
          id="${fieldConfig.id}"
          name="${fieldConfig.id}"
          ${disabled}
          ${required}
          placeholder="${placeholder}"
          class="wide select tqf_select form-control customers-based-select ${alignment}"
        >
          <option value="">${placeholder}</option>
          ${options}
        </select>
      </div>
    `;
  }

  /**
   * Render checkbox/radio field
   * @param {Object} fieldConfig - Field configuration
   * @param {Object} activeCustomer - Active customer data
   * @param {Object} behalfContext - Behalf context (optional)
   * @returns {string}
   */
  static renderCheckboxRadio(fieldConfig, activeCustomer, behalfContext = null, formSourceContext = null) {
    const dbl = this.getVisibilityClass(fieldConfig);
    // Do not add HTML required to checkbox inputs: browser would require every one to be checked.
    // For checkbox groups we validate "at least one" in JS only. Radio keeps required on inputs.
    const required = (fieldConfig.type === 'checkbox')
      ? ''
      : (fieldConfig.required === 'y' && dbl === 'd-block' ? 'required' : '');
    const readonly = fieldConfig.readonly === 'y' ? 'readonly' : '';
    const displayType = fieldConfig.dispaytype == 1 ? 'form-check-inline checkbox-inline' : '';
    const title = FormatUtils.escapeHtml(fieldConfig.title || '');
    const titleWithRequired = fieldConfig.required === 'y' && !title.includes('Required Field') 
      ? `${title} *` 
      : title;

    const rawValue = this.getValueForRender(fieldConfig, activeCustomer, behalfContext, formSourceContext);
    const isCheckbox = fieldConfig.type === 'checkbox';
    const selectedValuesLower = new Set();
    if (isCheckbox && rawValue != null) {
      const arr = Array.isArray(rawValue)
        ? rawValue
        : String(rawValue).split(',').map(s => s.trim()).filter(Boolean);
      arr.forEach(v => selectedValuesLower.add(String(v).toLowerCase()));
    }

    const options = (fieldConfig.values || []).map((dval, index) => {
      const optionValue = fieldConfig.email_options && fieldConfig.email_options[index]
        ? fieldConfig.email_options[index]
        : dval;
      const escapedValue = FormatUtils.escapeHtml(optionValue);
      const escapedLabel = FormatUtils.escapeHtml(dval);
      const nameSuffix = isCheckbox ? '[]' : '';
      const isChecked = isCheckbox
        ? selectedValuesLower.has(String(optionValue).toLowerCase())
        : (rawValue != null && String(rawValue).trim() !== '' && String(optionValue).toLowerCase() === String(rawValue).trim().toLowerCase());
      const checkedAttr = isChecked ? 'checked' : '';

      return `
        <div class="form-check ${displayType}">
          <input 
            class="form-check-input" 
            name="${fieldConfig.id}${nameSuffix}" 
            type="${fieldConfig.type}" 
            id="chk${fieldConfig.id}${index}" 
            value="${escapedValue}" 
            ${checkedAttr}
            ${readonly}
            ${required}
          >
          <label class="form-check-label" for="chk${fieldConfig.id}${index}">${escapedLabel}</label>
        </div>
      `;
    }).join('');

    return `
      <div class="form-group ${dbl}">
        <label>${titleWithRequired}</label>
        ${options}
      </div>
    `;
  }

  /**
   * Render file field
   * @param {Object} fieldConfig - Field configuration
   * @param {Object} activeCustomer - Active customer data
   * @param {Object} behalfContext - Behalf context (optional)
   * @returns {string}
   */
  static renderFile(fieldConfig, activeCustomer, behalfContext = null, formSourceContext = null) {
    const dbl = this.getVisibilityClass(fieldConfig);
    const required = this.getRequiredAttr(fieldConfig, dbl);
    const readonly = fieldConfig.readonly === 'y' ? 'readonly' : '';
    const title = FormatUtils.escapeHtml(fieldConfig.title || '');
    const titleWithRequired = fieldConfig.required === 'y' && !title.includes('Required Field') 
      ? `${title} *` 
      : title;

    return `
      <div class="form-group ${dbl}">
        <label for="${fieldConfig.id}">${titleWithRequired}</label>
        <input 
          id="${fieldConfig.id}" 
          name="${fieldConfig.id}" 
          class="form-control" 
          type="file"
          ${readonly}
          ${required}
        />
      </div>
    `;
  }

  /**
   * Render signature field
   * @param {Object} fieldConfig - Field configuration
   * @param {Object} activeCustomer - Active customer data
   * @param {Object} behalfContext - Behalf context (optional)
   * @returns {string}
   */
  static renderSignature(fieldConfig, activeCustomer, behalfContext = null, formSourceContext = null) {
    const dbl = this.getVisibilityClass(fieldConfig);
    const rawValue = this.getValueForRender(fieldConfig, activeCustomer, behalfContext, formSourceContext);
    const value = Array.isArray(rawValue) ? rawValue.join(', ') : String(rawValue ?? '');
    const title = FormatUtils.escapeHtml(fieldConfig.title || '');
    const t = this.translator ? (key) => this.translator.t(key) : ((key) => key);
    const signButtonText = t('buttons.click_to_sign');

    return `
      <div class="form-group ${dbl}">
        <span>${title}</span>
        <div>
          <input 
            id="${fieldConfig.id}" 
            name="${fieldConfig.id}" 
            class="form-control signature_placeholder" 
            type="hidden" 
            required 
            value="${FormatUtils.escapeHtml(value)}"
          />
          <button type="button" class="btn btn-warning sign_me" data-id="${fieldConfig.id}">${FormatUtils.escapeHtml(signButtonText)}</button>
        </div>
      </div>
    `;
  }

  /**
   * Render calculated field
   * @param {Object} fieldConfig - Field configuration
   * @param {Object} activeCustomer - Active customer data
   * @returns {string}
   */
  static renderCalculated(fieldConfig, activeCustomer) {
    const dbl = this.getVisibilityClass(fieldConfig);
    const title = FormatUtils.escapeHtml(fieldConfig.title || '');

    return `
      <div class="form-group ${dbl}">
        <label for="d${fieldConfig.id}">${title}</label>
        <div>
          <input 
            type="text" 
            readonly 
            disabled 
            id="d${fieldConfig.id}" 
            name="${fieldConfig.id}" 
            class="form-control" 
          />
        </div>
      </div>
    `;
  }

  /**
   * Render shadow field
   * @param {Object} fieldConfig - Field configuration
   * @param {Object} activeCustomer - Active customer data
   * @returns {string}
   */
  static renderShadow(fieldConfig, activeCustomer) {
    const dbl = this.getVisibilityClass(fieldConfig);
    const title = FormatUtils.escapeHtml(fieldConfig.title || '');

    return `
      <div class="form-group ${dbl}">
        <label>${title}</label>
        <div>
          <input 
            type="text" 
            id="d${fieldConfig.id}" 
            name="${fieldConfig.id}" 
            class="form-control" 
            readonly
          />
        </div>
      </div>
    `;
  }

  /**
   * Render button field
   * @param {Object} fieldConfig - Field configuration
   * @param {Object} activeCustomer - Active customer data
   * @returns {string}
   */
  static renderButton(fieldConfig, activeCustomer) {
    const dbl = this.getVisibilityClass(fieldConfig);
    const readonly = fieldConfig.readonly === 'y' ? 'readonly' : '';
    const buttonType = fieldConfig.buttontype || 'button';
    const alignment = fieldConfig.alignment || '';
    const title = FormatUtils.escapeHtml(fieldConfig.title || '');

    return `
      <button 
        type="${buttonType}" 
        ${readonly}
        class="btn btn-primary ${alignment} ${dbl}"
      >
        ${title}
      </button>
    `;
  }
}